/**
 * Tenant roles and the dashboard API's role policy.
 *
 * The table lives here rather than in `apps/dashboard-api` so the dashboard
 * SPA can hide a control using the same rule the API enforces, instead of a
 * copy of it that drifts (task 181). The API applies it in
 * `apps/dashboard-api/src/lib/authorization.ts`, whose tests hold the table to
 * what we intend.
 *
 * It is a subpath of its own, `@headerhawk/core/roles`, because the package
 * root re-exports Tinybird and event code a browser bundle has no business
 * loading.
 */
/** Roles are totally ordered; a higher rank subsumes every lower one. */
export const ROLE_RANK = {
    viewer: 0,
    member: 1,
    admin: 2,
    owner: 3,
};
/** True when `value` names one of the four roles. */
export function isUserRole(value) {
    return typeof value === "string" && Object.hasOwn(ROLE_RANK, value);
}
/** True when `role` is `minimum` or above. */
export function hasMinimumRole(role, minimum) {
    return ROLE_RANK[role] >= ROLE_RANK[minimum];
}
/**
 * The roles that satisfy `minimum`, highest first, for copy: "owners, admins
 * and members". Shared so the dashboard's explanations name the same roles as
 * the API's 403 message.
 */
export function describeRolesAtOrAbove(minimum) {
    const names = Object.keys(ROLE_RANK)
        .filter((role) => hasMinimumRole(role, minimum))
        .sort((a, b) => ROLE_RANK[b] - ROLE_RANK[a])
        .map((role) => `${role}s`);
    if (names.length === 1)
        return names[0];
    return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}
/**
 * Authorization policy for every operation the dashboard API implements, keyed
 * `METHOD /openapi/{path}` — the same key shape as `IMPLEMENTED_OPERATIONS` in
 * `apps/dashboard-api/src/handler.ts`, so the two can be diffed mechanically.
 *
 * Defaults applied here:
 * - Reads (`GET`) are open to every authenticated role.
 * - Changes to tenant-wide configuration or billing require owner/admin.
 * - Content-level changes (sites, alert rules) require member or above.
 * - Self-service edits to one's own profile are open to every role.
 */
export const ROLE_POLICY = {
    // --- Unauthenticated surface -------------------------------------------
    "GET /health": "public",
    "POST /waitlist": "public",
    "POST /contact": "public",
    // Authenticity comes from the Stripe webhook signature, not a session.
    "POST /webhooks/stripe": "public",
    // Authenticity comes from GitHub's secret scanning signature (task 442).
    "POST /webhooks/github/secret-scanning": "public",
    // Authenticity comes from the signed OAuth `state`, not a session.
    "GET /integrations/slack/callback": "public",
    // Provisioning: runs before a tenant (and therefore a role) exists.
    "POST /auth/me": "jwt",
    // --- Tenant configuration ----------------------------------------------
    // Onboarding: names the organization and provisions its Descope org.
    "POST /tenants/setup": "owner",
    "PATCH /tenant": "admin",
    // --- Verified email domains (task 347) ----------------------------------
    // A claim is tenant-wide configuration, and task 348 turns a verified one
    // into "which users an IdP may authenticate", so the writes are admin — the
    // same weight as renaming the organization or adding a destination. Reading
    // is open to every member: the list is what tells someone why SSO is not
    // working yet, and the expected record is not a secret.
    "GET /tenant/domains": "member",
    "POST /tenant/domains": "admin",
    // Runs a DNS lookup and may set `verified_at`, so it is a write.
    "POST /tenant/domains/{domainId}/verify": "admin",
    "DELETE /tenant/domains/{domainId}": "admin",
    // --- Single sign-on (task 348) ------------------------------------------
    // Tenant-wide authentication configuration, so admin — the same weight as
    // the domains an SSO connection is built from. The delete is **owner**:
    // after task 349 it is the control that decides whether a company's staff
    // can get in at all, and an admin should not be able to sever a company's
    // own identity provider on their own. Every one of the three is also gated
    // on the `auth.sso` capability, by `requireSso` in the handler — a role
    // policy cannot express a plan.
    "GET /tenant/sso": "admin",
    // Starting single sign-on gives the tenant its Descope organization
    // (task 510), so it is a write at the configuring role, not a read.
    "POST /tenant/sso/setup": "admin",
    "PUT /tenant/sso": "admin",
    "DELETE /tenant/sso": "owner",
    // Owner-only, like the delete: requiring the identity provider is the
    // control that decides whether a company's staff can sign in at all
    // (task 349).
    "PATCH /tenant/sso/enforcement": "owner",
    // --- API keys (task 437, ADR 014) --------------------------------------
    // Reading the list is open to every member, as the domains are: it holds no
    // secret, and it is what tells someone which system a key belongs to.
    // Issuing and revoking are tenant-wide configuration, so admin, like adding
    // an alert destination. Creating is also gated on `api.access` in the
    // handler; revoking is not, so a downgraded tenant can still clear keys out.
    "GET /api-keys": "viewer",
    "POST /api-keys": "admin",
    "DELETE /api-keys/{keyId}": "admin",
    // A member's own grants to assistants (task 440): theirs whatever the role.
    "GET /connected-assistants": "viewer",
    "DELETE /connected-assistants/{assistantId}": "viewer",
    // --- Billing ------------------------------------------------------------
    "POST /checkout/session": "admin",
    // Adds a card to the current subscription, so a trial can change plans.
    "POST /checkout/payment-method": "admin",
    // Post-redirect verification of a session only an admin could have started.
    "POST /checkout/verify": "admin",
    "POST /billing/portal": "admin",
    "GET /billing/subscription": "viewer",
    "POST /subscription/update": "admin",
    "GET /usage": "viewer",
    // --- Internal -----------------------------------------------------------
    "POST /internal/test-error": "admin",
    // --- Integrations -------------------------------------------------------
    // A GET, but it starts the OAuth connect flow, so it is gated as a mutation.
    "GET /integrations/slack/authorize": "admin",
    "GET /integrations/slack/channels": "viewer",
    "GET /integrations/slack": "viewer",
    "DELETE /integrations/slack": "admin",
    // --- Feedback -----------------------------------------------------------
    // Deliberately open: anyone using the product can report a problem.
    "POST /feedback": "viewer",
    // --- Reports explorer ---------------------------------------------------
    "GET /reports": "viewer",
    "GET /reports/histogram": "viewer",
    "GET /reports/facets": "viewer",
    "GET /reports/count": "viewer",
    "GET /reports/grouped": "viewer",
    "GET /reports/types": "viewer",
    "GET /reports/{reportId}": "viewer",
    // --- Team management ----------------------------------------------------
    "GET /users": "viewer",
    "POST /users/invite": "admin",
    "PATCH /users/{userId}": "admin",
    "DELETE /users/{userId}": "admin",
    // --- Own profile (self-service, open to every role) ---------------------
    "GET /me": "viewer",
    "PATCH /me": "viewer",
    // --- Policy › Destinations (task 366) -----------------------------------
    "GET /destinations": "viewer",
    // Policy › Frames (task 367)
    "GET /frames": "viewer",
    // --- Script analytics ---------------------------------------------------
    "GET /scripts/stats": "viewer",
    "GET /scripts/origins": "viewer",
    "GET /scripts/inventory": "viewer",
    "GET /scripts/pages": "viewer",
    // --- Browser changes (task 313) -----------------------------------------
    "GET /browser-changes": "viewer",
    "GET /browser-changes/histogram": "viewer",
    // --- Reliability (task 314) ---------------------------------------------
    "GET /reliability/network-errors": "viewer",
    "GET /reliability/network-errors/histogram": "viewer",
    "GET /reliability/crashes": "viewer",
    "GET /reliability/crashes/histogram": "viewer",
    // --- Issues -------------------------------------------------------------
    "GET /issues": "viewer",
    "GET /sites/{siteId}/issues": "viewer",
    "GET /sites/{siteId}/issues/{signature}/reports": "viewer",
    // Deciding on an issue (task 448): the same role as deciding on a script.
    "POST /sites/{siteId}/issues/{signature}/decision": "member",
    "GET /sites/{siteId}/issues/{signature}/decisions": "viewer",
    "GET /sites/{siteId}/noise-stats": "viewer",
    // --- Sites --------------------------------------------------------------
    // Member and above, matching what the product already tells people: the
    // invite dialog describes the member role as "Members can view and manage
    // sites" (`apps/web/src/components/settings/invite-user-dialog.tsx`).
    // Tightening these to owner/admin would be a product change, not a security
    // fix — BE-H2 is about `viewer`, who could delete a site and rotate its
    // ingest code, breaking live ingestion for the whole tenant.
    "GET /sites": "viewer",
    "POST /sites": "member",
    "GET /sites/{siteId}": "viewer",
    "PATCH /sites/{siteId}": "member",
    "DELETE /sites/{siteId}": "member",
    "POST /sites/{siteId}/rotate-key": "member",
    // Choosing or releasing a site's report host (task 421) can stop a published
    // URL working, like rotation, so it is gated like rotation.
    "PUT /sites/{siteId}/ingest-label": "member",
    "DELETE /sites/{siteId}/ingest-label": "member",
    // Report hosts on the customer's own domain (task 422). Adding or removing
    // one changes what a DNS record of the customer's means, so it is tenant
    // configuration, like verified domains.
    "GET /sites/{siteId}/hosts": "viewer",
    "POST /sites/{siteId}/hosts": "admin",
    "POST /sites/{siteId}/hosts/{hostId}/retry": "admin",
    "DELETE /sites/{siteId}/hosts/{hostId}": "admin",
    "GET /sites/{siteId}/setup": "viewer",
    // Policy discovery (task 407): starting or stopping a run changes what the
    // site collects and spends its allowance, a content-level change like a
    // monitored page.
    "GET /sites/{siteId}/next-step": "viewer",
    "PUT /sites/{siteId}/next-step": "member",
    // Task 498: fetches the site's page, and spends the site's and tenant's
    // hourly checks, so a viewer, who changes nothing, does not.
    "POST /sites/{siteId}/header-check": "member",
    // Task 512: mails the member their own site's step, which spends their
    // hourly emails, so a viewer, who has no step to take, does not.
    "POST /sites/{siteId}/next-step/email": "member",
    "GET /sites/{siteId}/policy-discovery": "viewer",
    "PUT /sites/{siteId}/policy-discovery": "member",
    "GET /sites/{siteId}/policy-discovery/proposal": "viewer",
    "PUT /sites/{siteId}/policy-discovery/decisions": "member",
    // A read of the site's own diagnostics. It discloses hosts that reported to
    // this site, which any member of the tenant can already see on the setup
    // page, so it is gated the same way as the rest of the site's reads.
    "GET /sites/{siteId}/dropped-reports": "viewer",
    // Monitored pages (task 306): marking which pages matter is day-to-day site
    // work, like editing the site's hosts. The plan gate is applied in the
    // routes, since it is about the tenant, not the caller's role.
    "GET /sites/{siteId}/pages": "viewer",
    "POST /sites/{siteId}/pages": "member",
    "PATCH /sites/{siteId}/pages/{pageId}": "member",
    "DELETE /sites/{siteId}/pages/{pageId}": "member",
    "GET /sites/{siteId}/page-suggestions": "viewer",
    "GET /sites/{siteId}/csp-policies": "viewer",
    "GET /sites/{siteId}/page-changes": "viewer",
    "GET /sites/{siteId}/pages/{pageId}/scans": "viewer",
    "GET /sites/{siteId}/pages/{pageId}/scans/{scanId}": "viewer",
    // Script authorization (task 307): reading the inventory and its evidence
    // is open to every role; deciding is member, like alert rules (Michael,
    // 2026-09-16). Every decision records who made it, which is what an
    // assessor checks.
    "GET /sites/{siteId}/scripts": "viewer",
    "GET /sites/{siteId}/scripts/inventory.csv": "viewer",
    // Scripts › Integrity's trend (task 375): the same evidence, by day.
    "GET /sites/{siteId}/scripts/sri-coverage/daily": "viewer",
    "GET /sites/{siteId}/scripts/payment-policy": "viewer",
    "GET /sites/{siteId}/scripts/{scriptId}": "viewer",
    "POST /sites/{siteId}/scripts/{scriptId}/decision": "member",
    // A captured script may be malicious by the time someone wants it, and each
    // download is written to the audit trail: evidence handling, which is the
    // owners' and admins' (task 359).
    "POST /sites/{siteId}/scripts/{scriptId}/capture/download": "admin",
    // Compliance (task 372): read-only by construction, and composed from
    // reads every role already has, so every role may give it to an assessor.
    "GET /compliance/pci-dss": "viewer",
    "GET /compliance/pci-dss/evidence.csv": "viewer",
    // The Overview (task 373): counts and links from reads every role has.
    "GET /overview": "viewer",
    // --- Alerting (task 304, ADR 010) ----------------------------------------
    // A destination decides where tenant data leaves HeaderHawk, the same weight
    // as connecting Slack, so its writes are admin. Choosing which existing
    // destinations hear about what is day-to-day work, so rules are member.
    "GET /alert-destinations": "viewer",
    "POST /alert-destinations": "admin",
    "PATCH /alert-destinations/{destinationId}": "admin",
    "DELETE /alert-destinations/{destinationId}": "admin",
    // Sends to a destination an admin already configured; it cannot change
    // where anything goes, so a member may check it works.
    "POST /alert-destinations/{destinationId}/test": "member",
    "GET /alert-rules": "viewer",
    "POST /alert-rules": "member",
    "PATCH /alert-rules/{ruleId}": "member",
    "DELETE /alert-rules/{ruleId}": "member",
    "GET /alert-event-types": "viewer",
    "GET /alert-deliveries": "viewer",
    "GET /sites/{siteId}/alert-coverage": "viewer",
    // Authenticity comes from the emailed token: the person clicking may have
    // no HeaderHawk account at all.
    "POST /alert-email-confirmations": "public",
    // The public demo (task 405). A proof-of-work answer is the only input;
    // the member and tenant it opens are deployment configuration.
    "GET /demo/challenge": "public",
    "POST /demo/session": "public",
};
/**
 * Whether `role` may call `operation`.
 *
 * `public` and `jwt` operations need less than any tenant role, so every role
 * may call them.
 */
export function canPerform(role, operation) {
    const policy = ROLE_POLICY[operation];
    if (policy === "public" || policy === "jwt")
        return true;
    return hasMinimumRole(role, policy);
}
/**
 * The roles that may call `operation`, for copy — "owners and admins" — so an
 * explanation names the same roles as the API's 403 for it.
 */
export function describeRolesAllowedTo(operation) {
    const policy = ROLE_POLICY[operation];
    if (policy === "public" || policy === "jwt")
        return "everyone";
    return describeRolesAtOrAbove(policy);
}
export const IMPERSONATION_POLICY = {
    "GET /health": "allow",
    "POST /waitlist": "deny",
    "POST /contact": "deny",
    "POST /webhooks/stripe": "deny",
    "POST /webhooks/github/secret-scanning": "deny",
    "GET /integrations/slack/callback": "deny",
    // The SPA's first call. Its impersonated branch returns the member without
    // provisioning, recording a login or syncing an email.
    "POST /auth/me": "allow",
    "POST /tenants/setup": "deny",
    "PATCH /tenant": "deny",
    "GET /tenant/domains": "allow",
    "POST /tenant/domains": "deny",
    "POST /tenant/domains/{domainId}/verify": "deny",
    "DELETE /tenant/domains/{domainId}": "deny",
    "GET /tenant/sso": "allow",
    "POST /tenant/sso/setup": "deny",
    "PUT /tenant/sso": "deny",
    "DELETE /tenant/sso": "deny",
    "PATCH /tenant/sso/enforcement": "deny",
    // A key is a credential the member would hold after the view ended, so
    // neither an operator nor a demo visitor may issue one; revoking is a write.
    "GET /api-keys": "allow",
    "POST /api-keys": "deny",
    "DELETE /api-keys/{keyId}": "deny",
    "GET /connected-assistants": "allow",
    // Revokes the member's own consent; an operator never acts for them.
    "DELETE /connected-assistants/{assistantId}": "deny",
    // Checkout and the portal open a Stripe session as the member.
    "POST /checkout/session": "deny",
    "POST /checkout/payment-method": "deny",
    "POST /checkout/verify": "deny",
    "POST /billing/portal": "deny",
    "GET /billing/subscription": "allow",
    "POST /subscription/update": "deny",
    "GET /usage": "allow",
    "POST /internal/test-error": "deny",
    // A GET, but it starts Slack's OAuth flow as the member.
    "GET /integrations/slack/authorize": "deny",
    "GET /integrations/slack/channels": "allow",
    "GET /integrations/slack": "allow",
    "DELETE /integrations/slack": "deny",
    // Sends an email from the member.
    "POST /feedback": "deny",
    "GET /reports": "allow",
    "GET /reports/histogram": "allow",
    "GET /reports/facets": "allow",
    "GET /reports/count": "allow",
    "GET /reports/grouped": "allow",
    "GET /reports/types": "allow",
    "GET /reports/{reportId}": "allow",
    "GET /users": "allow",
    "POST /users/invite": "deny",
    "PATCH /users/{userId}": "deny",
    "DELETE /users/{userId}": "deny",
    "GET /me": "allow",
    "PATCH /me": "deny",
    "GET /destinations": "allow",
    "GET /frames": "allow",
    "GET /scripts/stats": "allow",
    "GET /scripts/origins": "allow",
    "GET /scripts/inventory": "allow",
    "GET /scripts/pages": "allow",
    "GET /browser-changes": "allow",
    "GET /browser-changes/histogram": "allow",
    "GET /reliability/network-errors": "allow",
    "GET /reliability/network-errors/histogram": "allow",
    "GET /reliability/crashes": "allow",
    "GET /reliability/crashes/histogram": "allow",
    "GET /issues": "allow",
    "GET /sites/{siteId}/issues": "allow",
    "GET /sites/{siteId}/issues/{signature}/reports": "allow",
    "POST /sites/{siteId}/issues/{signature}/decision": "deny",
    "GET /sites/{siteId}/issues/{signature}/decisions": "allow",
    "GET /sites/{siteId}/noise-stats": "allow",
    "GET /sites": "allow",
    "POST /sites": "deny",
    "GET /sites/{siteId}": "allow",
    "PATCH /sites/{siteId}": "deny",
    "DELETE /sites/{siteId}": "deny",
    "POST /sites/{siteId}/rotate-key": "deny",
    "PUT /sites/{siteId}/ingest-label": "deny",
    "DELETE /sites/{siteId}/ingest-label": "deny",
    "GET /sites/{siteId}/hosts": "allow",
    "POST /sites/{siteId}/hosts": "deny",
    "POST /sites/{siteId}/hosts/{hostId}/retry": "deny",
    "DELETE /sites/{siteId}/hosts/{hostId}": "deny",
    "GET /sites/{siteId}/setup": "allow",
    "GET /sites/{siteId}/next-step": "allow",
    "PUT /sites/{siteId}/next-step": "deny",
    // Read-only in effect, but it spends the tenant's hourly checks (task 498).
    "POST /sites/{siteId}/header-check": "deny",
    // Sends mail as the member (task 512).
    "POST /sites/{siteId}/next-step/email": "deny",
    "GET /sites/{siteId}/policy-discovery": "allow",
    "PUT /sites/{siteId}/policy-discovery": "deny",
    "GET /sites/{siteId}/policy-discovery/proposal": "allow",
    "PUT /sites/{siteId}/policy-discovery/decisions": "deny",
    "GET /sites/{siteId}/dropped-reports": "allow",
    "GET /sites/{siteId}/pages": "allow",
    "POST /sites/{siteId}/pages": "deny",
    "PATCH /sites/{siteId}/pages/{pageId}": "deny",
    "DELETE /sites/{siteId}/pages/{pageId}": "deny",
    "GET /sites/{siteId}/page-suggestions": "allow",
    "GET /sites/{siteId}/csp-policies": "allow",
    "GET /sites/{siteId}/page-changes": "allow",
    "GET /sites/{siteId}/pages/{pageId}/scans": "allow",
    "GET /sites/{siteId}/pages/{pageId}/scans/{scanId}": "allow",
    "GET /sites/{siteId}/scripts": "allow",
    // Pure reads: neither CSV names who exported it or writes anything.
    "GET /sites/{siteId}/scripts/inventory.csv": "allow",
    "GET /sites/{siteId}/scripts/sri-coverage/daily": "allow",
    "GET /sites/{siteId}/scripts/payment-policy": "allow",
    "GET /sites/{siteId}/scripts/{scriptId}": "allow",
    "POST /sites/{siteId}/scripts/{scriptId}/decision": "deny",
    // A read, but `script_capture_downloads` would name the member as the
    // person who downloaded it.
    "POST /sites/{siteId}/scripts/{scriptId}/capture/download": "deny",
    "GET /compliance/pci-dss": "allow",
    "GET /compliance/pci-dss/evidence.csv": "allow",
    "GET /overview": "allow",
    "GET /alert-destinations": "allow",
    "POST /alert-destinations": "deny",
    "PATCH /alert-destinations/{destinationId}": "deny",
    "DELETE /alert-destinations/{destinationId}": "deny",
    // Sends a message to the customer's destination.
    "POST /alert-destinations/{destinationId}/test": "deny",
    "GET /alert-rules": "allow",
    "POST /alert-rules": "deny",
    "PATCH /alert-rules/{ruleId}": "deny",
    "DELETE /alert-rules/{ruleId}": "deny",
    "GET /alert-event-types": "allow",
    "GET /alert-deliveries": "allow",
    "GET /sites/{siteId}/alert-coverage": "allow",
    "POST /alert-email-confirmations": "deny",
    "GET /demo/challenge": "allow",
    // Mints a session; a session never needs to mint another.
    "POST /demo/session": "deny",
};
/**
 * Whether an impersonated session may call `operation`. For the SPA, which
 * hides a write control the API would refuse; the API applies the same table.
 */
export function allowsImpersonation(operation) {
    return IMPERSONATION_POLICY[operation] === "allow";
}
/**
 * The dashboard operations whose logic a credential with no member behind it
 * may reach (ADR 014): an API key through the public API (task 438), and an
 * MCP session through the MCP server's tools (task 440).
 *
 * Being `viewer` is necessary, not sufficient. Several `viewer` reads return
 * what an integration has no business holding — alert destinations carry
 * webhook URLs and Slack channels — and a dashboard operation added later
 * must not become reachable by a key because someone gave it `viewer`. So a
 * key reads only what this list names, and the public API's handler refuses
 * anything else, failing closed, the way `enforceImpersonationPolicy` does.
 *
 * `packages/core/tests/roles.test.ts` fails on an entry that is not `viewer`
 * in `ROLE_POLICY` and `allow` in `IMPERSONATION_POLICY`, so this list can
 * only narrow what a read-only session already may.
 */
export const PUBLIC_READ_OPERATIONS = [
    "GET /sites",
    "GET /sites/{siteId}",
    "GET /issues",
    "GET /sites/{siteId}/issues",
    "GET /sites/{siteId}/issues/{signature}/reports",
    // Task 439: the Reports Explorer, Reliability and Browser changes.
    "GET /reports",
    "GET /reports/count",
    "GET /reports/grouped",
    "GET /reports/histogram",
    "GET /reports/types",
    "GET /reliability/network-errors",
    "GET /reliability/crashes",
    "GET /browser-changes",
    // Task 439: monitored pages, their scans, the CSP policies reports quoted
    // and the alert delivery history. Not alert destinations or rules, whose
    // configuration names webhook URLs and Slack channels.
    "GET /sites/{siteId}/pages",
    "GET /sites/{siteId}/pages/{pageId}/scans",
    "GET /sites/{siteId}/pages/{pageId}/scans/{scanId}",
    "GET /sites/{siteId}/csp-policies",
    "GET /alert-deliveries",
    // The script inventory and PCI DSS readiness. Not the captured script
    // download, a POST that only owners and admins may make, audited per
    // download.
    "GET /sites/{siteId}/scripts",
    "GET /sites/{siteId}/scripts/{scriptId}",
    "GET /compliance/pci-dss",
    // Task 465: the policy proposed from a site's discovery run, for the MCP
    // server's `get_policy_proposal`. Not the decisions, a write.
    "GET /sites/{siteId}/policy-discovery/proposal",
    // `GET /v1/me`: the key's own record, which is a row of `api_keys`.
    "GET /api-keys",
];
/** Whether a credential with no member behind it may reach `operation`. */
export function isPublicReadOperation(operation) {
    return PUBLIC_READ_OPERATIONS.includes(operation);
}
/**
 * The custom claim an impersonation session carries (task 394), set by the
 * admin API through Descope's management API and nowhere else. Only a
 * management-key call can put a custom claim on an impersonation token, and
 * every flow in a customer project is applied from the repo, so no customer
 * can produce one.
 */
export const IMPERSONATION_CLAIM = "hhImpersonation";
/**
 * The custom claim a public demo session carries instead (task 405), set by
 * the dashboard API's `POST /demo/session` through the same management API.
 * No operator acted, so there is no audit row to name. The dashboard API
 * accepts it only for the configured demo member in the configured demo
 * tenant, and only on the demo stage.
 */
export const DEMO_CLAIM = "hhDemo";
//# sourceMappingURL=index.js.map